import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Signup.css';
import { Popup } from '../dashboard/components/Popup';
import ENV from '../constants/env-constants';
import ReactGA from "react-ga4";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Helmet } from 'react-helmet';
import { signupUser, createFreeTreeTransaction } from '../utils/api';
import { pageAndRouteMapping } from '../utils/constants';
import Spinner from '../components/Spinner/Spinner';

function isDifferenceGreaterThanOneDay(date1, date2) {
  const msInOneDay = 24 * 60 * 60 * 1000; // Number of milliseconds in one day
  const diffInMs = Math.abs(date2.getTime() - date1.getTime());
  console.log(msInOneDay, diffInMs/60000);
  return diffInMs > msInOneDay;
}

function Signup() {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  useEffect(()=>{
    ReactGA.send({ hitType: "pageview", page: location.pathname, title: 'Signup Page' });
    if (queryParams.get('sm') === '1') {
        localStorage.removeItem('sm');
        localStorage.setItem('sm', JSON.stringify({ value: 1, creationTime: new Date() }))
    }
  }, []);
  const [customErrorMessage, setCustomErrorMessage] = useState(''); 
  const [showPopup, setShowPopup] = useState(false);
  const [message, setMessage] = useState(['Thank you.','We received your request to setup a new account. You are a valuable member of our growing community.','You may now login to your account, receive special news and updates, and track the progress of your forest as we work toward a common goal.']);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  function navigateToLoginPage() {
    navigate(pageAndRouteMapping.SIGNIN);
  }
  const handleSubmit = async (values) => {
    try {
      setIsButtonLoading(true);
      const response = await signupUser(values);
      const data = await response.json();
      console.log("------------data")
      console.log(data);
      if (queryParams.get('sm') === '1' ||
      ((localStorage.getItem('sm') !== null) &&
      (JSON.parse(localStorage.getItem('sm')).value === 1) &&
      (!isDifferenceGreaterThanOneDay(new Date(JSON.parse(localStorage.getItem('sm')).creationTime), new Date())))) {
        await createFreeTreeTransaction(data.userDetails.userId, data.userDetails.email);
        localStorage.removeItem('sm');
      }
      if(response.ok) {
        navigate(pageAndRouteMapping.SIGNUP_SUCCESS)
      } else {
        // Properly wait for the error message to be parsed from JSON
        const errorData = await response.json(); // This is a promise, so we await it
        // Assuming your backend sends a clear error message under a specific key, e.g., errorData.message
        setCustomErrorMessage(errorData.message || "An unexpected error occurred. Please try again."); // Adjust based on your API
      }
    } catch (error) {
      console.error('Signup failed:', error);
      // Since 'error' is an object, we need to convert it to a string to display it
      setCustomErrorMessage(error.message || "An error occurred. Please try again.");
    }
    setIsButtonLoading(false);
  };
  return (
    <div className="maincontsignup">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Sign Up</title>
      </Helmet>
      <div className="signup-container">
          <div className="signup-info">
              <p>Please sign up with Blue Planet Ecosystem to receive benefits:</p>
              <p>In signing up, you become a valuable member of our global community and will receive your own personalized secure login.</p>
              <ul>
                  <li>Track your investment and donation history - see how your support creates your own forest, sequestering CO2 into oxygen for our environment.</li>
                  <li>Receive tailored updates on the progress of tree planting initiatives - receive updates and alerts with exclusive information about new projects, environmental initiatives, and success stories related to our community initiatives, environment restoration, and mangrove planting.</li>
              </ul>
              <p>Join us!</p>
          </div>
          <div className="centercustom">
            <h1>Create Your Account</h1>
            <FormComponent 
              handleSubmit={handleSubmit}
              customErrorMessage={customErrorMessage}
              isButtonLoading={isButtonLoading}
              buttonText="Sign Up"
            />
            {showPopup && <Popup buttonText="Go To Login Page" showButton buttonClickHandler={navigateToLoginPage} message={message} onClose={() => setShowPopup(false)} />}
          </div>
      </div>
    </div>

  );
}

const signUpFieldValidationSchemaObject = {
  firstName: Yup.string().required('First Name is required').max(50, 'Max Character limit reached'),
  lastName: Yup.string().required('Last Name is required').max(50, 'Max Character limit reached'),
  companyOrOrganization: Yup.string().max(100, 'Max Character limit reached'),
  email: Yup.string()
    .required('Email is required')
    .max(320, 'Max Character limit reached')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email address'),
  zipcode: Yup.string().required('Zipcode is required').max(15, 'Max Character limit reached'),
  country: Yup.string().required('Country is required'),
  userType: Yup.string().required('Category is required'),
  password: Yup.string().required('Password is required').min(8, 'Password must be at least 8 characters long').max(20, 'Max Character limit reached'),
}

function getSignUpFieldValidationSchema(isRequiredInAdminDashboard) {
  let schemaObject = {...signUpFieldValidationSchemaObject};
  if (isRequiredInAdminDashboard) {
    delete schemaObject.password
  }
  const signUpFieldValidationSchema = Yup.object().shape(schemaObject);
  return signUpFieldValidationSchema;
}

function FormComponent({handleSubmit, customErrorMessage, isButtonLoading, buttonText, isRequiredInAdminDashboard}) {
  const initialFormValues = {
    firstName: '',
    lastName: '',
    companyOrOrganization: '',
    email: '',
    zipcode: '',
    country: '',
    userType: '',
    password: '',
  };
  if (isRequiredInAdminDashboard) delete initialFormValues.password;
  const formik = useFormik({
    initialValues: {...initialFormValues},
    onSubmit: (values) => {
      handleSubmit(values);
    },
    validationSchema: getSignUpFieldValidationSchema(isRequiredInAdminDashboard)
  });

  return <form className='form'>
    <InputComponent inputType="text" formik={formik} placeholder="First Name" property="firstName" />
    <InputComponent inputType="text" formik={formik} placeholder="Last Name" property="lastName" />
    <InputComponent inputType="text" formik={formik} placeholder="Company or Organization Name" property="companyOrOrganization" />
    <InputComponent inputType="email" formik={formik} placeholder="Email" property="email" />
    <InputComponent inputType="text" formik={formik} placeholder="Zipcode" property="zipcode" />
    <div className="txt_field">
      <select
        value={formik.values.country}
        className="form-select mb-4"
        style={{width: '100%'}}
        id="country"
        name="country"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      >
        <option value="" disabled>Country</option>
        <option value="United States">United States</option>
        <option value="Australia">Australia</option>
        <option value="Canada">Canada</option>
        <option value="New Zealand">New Zealand</option>
        <option value="South Africa">South Africa</option>
        <option value="United Kingdom">United Kingdom</option>
      </select>
    </div>
    <div>
      {formik.touched.country && formik.errors.country && <p style={{ color: 'red', margin: '0', padding: '0' }}>{formik.errors.country}</p>}
    </div>
    <div className="txt_field">
      <select
        id="userType"
        name="userType"
        value={formik.values.userType}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className="form-select mb-4"
        aria-label="User type"
      >
        <option value="" disabled>Category</option>
        <option value="individual">Individuals</option>
        <option value="corporate">Corporate Partners</option>
        <option value="educational">Educational Institutions</option>
        <option value="foundations">Foundations and Trusts</option>
      </select>
    </div>
    <div>
      {formik.touched.userType && formik.errors.userType && <p style={{ color: 'red', margin: '0', padding: '0' }}>{formik.errors.userType}</p>}
    </div>
    { !isRequiredInAdminDashboard ? <InputComponent inputType="password" formik={formik} placeholder="Password" property="password" /> : null }
    <div className="error-message" style={{color:'red'}}>{customErrorMessage}</div>
    <div onClick={formik.handleSubmit} className='sign-in-submit-button' style={{height: '3rem', display: 'flex', justifyContent: 'center', padding: '0.5rem', alignItems: 'center'}}>
      {isButtonLoading ? <Spinner color="#2691d9"/> : buttonText}
    </div>
  </form>
}

function InputComponent({formik, property, placeholder, inputType}) {
  return <div>
            <div className="txt_field">
              <input
                type={inputType}
                name={property}
                value={formik.values[property]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className='custominput'
              />
              <span />
              <label>{placeholder}</label>
            </div>
            <div>
              {formik.touched[property] && formik.errors[property] && <p style={{ color: 'red', margin: '0', padding: '0' }}>{formik.errors[property]}</p>}
            </div>
          </div>
}

export default Signup;
export { FormComponent as SignupFormComponent, InputComponent as InputComponentAsSignupStyle }